import React, { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { CustomTabItems, CustomTabContent } from "./CustomTabs";
import CustomPaper from "../common/customPaper";
import FiltersButton from "../common/FiltersButton";
import { DateRangePicker } from "../filters/DateRangePicker";
import { convertToDateRange } from "../../utils/commonUtils";
import moment from "moment";

const RenderCustomElm = React.memo(
    ({ tab }) => {
        const CustomElement = tab.customElmInTabHeader;
        return CustomElement ? <CustomElement /> : null;
    },
    (prevProps, nextProps) => {
        return prevProps.tab === nextProps.tab;
    }
);

const FilterableTabCard = (props) => {
    const {
        tabs,
        onTabClick,
        onFilterClick,
        appliedFilters,
        filterCount,
        setRequiredFilters,
        value,
    } = props;
    const [dateRange, setDateRange] = useState({});

    const handleChange = (event, newValue) => {
        if (onTabClick) {
            onTabClick(newValue);
        }
    };

    useEffect(() => {
        if (appliedFilters?.initialDateRange) {
            setDateRange(convertToDateRange(appliedFilters?.initialDateRange));
        }
    }, [appliedFilters]);

    const handleDateRangeChange = useCallback(
        (startDate, endDate) => {
            setDateRange({ startDate, endDate });

            appliedFilters.initialDateRange = {
                startDate,
                endDate
            };
        },
        [appliedFilters]
    );

    const currentTab = useMemo(() => tabs[value], [tabs, value]);
    return (
        <CustomPaper alignItems="left">
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                my={0.5}
            >
                <CustomTabItems
                    tabs={tabs}
                    value={value}
                    handleChange={handleChange}
                />
                <Box display="flex" alignItems="center">
                    {currentTab?.customElmInTabHeader && (
                        <RenderCustomElm tab={currentTab} />
                    )}
                    <Box marginRight={2}>
                        {currentTab?.showDateRange && (
                            <DateRangePicker
                                dateRange={dateRange}
                                onDateRangeChange={handleDateRangeChange}
                            />
                        )}
                    </Box>
                    {currentTab?.showFilter && (
                        <FiltersButton
                            onClick={onFilterClick}
                            filtersCount={filterCount}
                        />
                    )}
                </Box>
            </Box>
            <CustomTabContent
                tabs={tabs}
                value={value}
                appliedFilters={appliedFilters}
                setRequiredFilters={setRequiredFilters}
            />
        </CustomPaper>
    );
};

FilterableTabCard.propTypes = {
    onTabClick: PropTypes.func,
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            content: PropTypes.node,
        })
    ).isRequired,
    initialDateRange: PropTypes.shape({
        startDate: PropTypes.string.isRequired,
        endDate: PropTypes.string.isRequired,
    }),
    children: PropTypes.node,
    appliedFilters: PropTypes.object,
};

export default FilterableTabCard;
